<div class="public-molst-lookup-container">
  <!-- Back link: ONLY when a signed-in session exists (clinician arrived from
       the app). Anonymous emergency responders see no app chrome at all. -->
  <a class="topbar-back" *ngIf="returnLink" [routerLink]="returnLink">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M15 18l-6-6 6-6"></path></svg>
    Back
  </a>

  <!-- Header — same page pattern as the rest of the app (eyebrow/title/lede). -->
  <div class="header">
    <div class="header-content">
      <p class="page__eyebrow">Emergency access</p>
      <h1 class="title">MOLST Quick Lookup</h1>
      <p class="subtitle">
        Scan a patient's QR code to see their Medical Orders for
        Life-Sustaining Treatment.
      </p>
    </div>
  </div>

  <!-- QR Code Verify (upload-first) -->
  <div class="scanner-section" *ngIf="showScanner">
    <div class="scanner-container">
      <h2 class="scanner-title">Verify a Patient QR Code</h2>
      <p class="scanner-instructions">
        Upload or drag in the QR code from the patient's card, wristband, or a screenshot.
        It is verified against the blockchain instantly.
      </p>

      <!-- Primary: upload / drag-and-drop a QR image (no camera needed) -->
      <div
        class="qr-dropzone"
        [class.dragover]="isDragging"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
      >
        <i class="pi pi-upload"></i>
        <p class="dz-title">Drag &amp; drop a QR code image here</p>
        <p class="dz-sub">or click to choose a file — PNG, JPG, or a screenshot</p>
        <input #fileInput type="file" accept="image/*" hidden (change)="onFileSelected($event)" />
      </div>

      <!-- Secondary: opt-in camera -->
      <div class="upload-divider"><span>or use the camera</span></div>
      <div *ngIf="!cameraOn" class="camera-optin">
        <button pButton type="button" class="p-button-outlined" (click)="enableCamera()">
          <i class="pi pi-camera"></i>&nbsp; Use camera instead
        </button>
      </div>
      <div class="video-container" *ngIf="cameraOn">
        <video #video autoplay playsinline></video>
        <div class="scanner-overlay">
          <div class="scan-frame"></div>
        </div>
      </div>

      <div class="scanner-actions">
        <button
          pButton
          type="button"
          class="p-button-secondary"
          (click)="resetScanner()"
          *ngIf="error"
        >
          <i class="pi pi-refresh"></i>
          Try Again
        </button>
      </div>

      <!-- Error Display -->
      <div class="error-message" *ngIf="error">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-section" *ngIf="isLoading">
    <div class="loading-content">
      <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
      <h3>Loading MOLST Form...</h3>
      <p>Please wait while we retrieve the patient's information</p>
    </div>
  </div>

  <!-- MINIMAL tier: emergency summary (this device is not registered) -->
  <div class="minimal-display" *ngIf="minimal && !isLoading">
    <app-emergency-summary-card [summary]="minimal"></app-emergency-summary-card>
  </div>

  <!-- MOLST Form Display -->
  <div class="molst-display" *ngIf="molstForm && !isLoading">
    <!-- Blockchain integrity + audit banner -->
    <div
      class="blockchain-banner"
      [ngStyle]="{
        border: '1px solid',
        'border-radius': '8px',
        padding: '12px 16px',
        margin: '0 0 16px',
        'font-size': '14px',
        'border-color':
          integrityResult === 'fail'
            ? '#dc2626'
            : integrityResult === 'pass'
            ? '#16a34a'
            : '#d97706',
        background:
          integrityResult === 'fail'
            ? '#fef2f2'
            : integrityResult === 'pass'
            ? '#f0fdf4'
            : '#fffbeb',
        color:
          integrityResult === 'fail'
            ? '#991b1b'
            : integrityResult === 'pass'
            ? '#166534'
            : '#92400e'
      }"
    >
      <strong *ngIf="integrityChecked && integrityResult === 'pass'">
        ✓ Verified on blockchain — this directive is authentic and unaltered.
      </strong>
      <strong *ngIf="integrityChecked && integrityResult === 'fail'">
        ⚠ Integrity check FAILED — this document does not match its blockchain
        record. Do not rely on it; contact the issuing facility.
      </strong>
      <strong *ngIf="integrityChecked && integrityResult === 'local_only'">
        ⚠ Blockchain confirmation unavailable — the document matches its stored
        record, but the blockchain could not be reached to confirm it.
      </strong>
      <strong *ngIf="integrityChecked && integrityResult === 'unknown'">
        ⚠ Not yet anchored on the blockchain — integrity could not be confirmed.
      </strong>
      <strong *ngIf="!integrityChecked && blockchainStatus === 'confirmed'">
        ✓ Recorded on blockchain (legacy link) — access has been audited.
      </strong>
      <strong *ngIf="!integrityChecked && blockchainStatus !== 'confirmed'">
        This access has been recorded on the blockchain audit ledger.
      </strong>

      <div style="margin-top:6px; font-weight:400; font-size:12px;">
        <span *ngIf="!isCurrentVersion" style="color:#b45309;">
          ⚠ This is not the current active version (superseded/withdrawn).
        </span>
        <span *ngIf="anchoredTxId">
          Blockchain tx: <code>{{ anchoredTxId }}</code> ·
        </span>
        Every access to this record is immutably logged.
      </div>
    </div>

    <div class="molst-header">
      <h2 class="molst-title">
        Medical Orders for Life-Sustaining Treatment (MOLST)
      </h2>

      <!-- Patient Information -->
      <div class="patient-info" *ngIf="molstForm.patient">
        <div class="patient-name">
          <span class="patient-label">Patient:</span>
          <span class="patient-value"
            >{{ molstForm.patient.firstName }}
            {{ molstForm.patient.lastName }}</span
          >
        </div>
        <div class="patient-dob">
          <span class="patient-label">Date of Birth:</span>
          <span class="patient-value">{{
            formatDate(molstForm.patient.dateOfBirth)
          }}</span>
        </div>
      </div>

      <!-- Form Meta Information -->
      <div class="form-meta">
        <div class="meta-item">
          <span class="meta-label">Form ID:</span>
          <span class="meta-value">{{ molstForm.id }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Status:</span>
          <span class="status-badge" [ngClass]="'status-' + molstForm.status">
            {{
              molstForm.status.charAt(0).toUpperCase() +
                molstForm.status.slice(1)
            }}
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Created:</span>
          <span class="meta-value">{{
            formatDate(molstForm.creationDate)
          }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Last Modified:</span>
          <span class="meta-value">{{
            formatDate(molstForm.lastModified)
          }}</span>
        </div>
      </div>
    </div>

    <!-- Form Sections -->
    <div class="form-sections">
      <!-- Section 1a: Basis of Consent -->
      <div class="form-section">
        <h3 class="section-title">
          Certification for the Basis of These Orders
        </h3>
        <div class="section-content">
          <div class="checkbox-grid">
            <div
              class="checkbox-item"
              *ngFor="
                let item of [
                  { key: 'patient', label: 'The patient' },
                  { key: 'healthCareAgent', label: 'Healthcare agent' },
                  {
                    key: 'guardianOrSurrogate',
                    label: 'Guardian or surrogate'
                  },
                  {
                    key: 'minorLegalGuardian',
                    label: 'Minor\'s legal guardian'
                  },
                  { key: 'advanceDirective', label: 'Advance directive' },
                  {
                    key: 'otherLegalAuthority',
                    label: 'Other legal authority'
                  },
                  {
                    key: 'declinesOrUnable',
                    label: 'Patient declines or is unable to make decisions'
                  }
                ]
              "
            >
              <div
                class="checkbox-display"
                [class.checked]="molstForm.basisOfConsent[item.key]"
              >
                <i
                  class="pi"
                  [class.pi-check]="molstForm.basisOfConsent[item.key]"
                  [class.pi-times]="!molstForm.basisOfConsent[item.key]"
                ></i>
              </div>
              <span class="checkbox-label">{{ item.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 1: CPR Status -->
      <div class="form-section">
        <h3 class="section-title">Section 1: CPR (Resuscitation) Status</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.cprStatus.cprOption === 'attemptCpr'"
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.cprStatus.cprOption === 'attemptCpr'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.cprStatus.cprOption === 'attemptCpr'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">1a)</span>
                Attempt CPR: If cardiac and/or pulmonary arrest occurs, attempt
                cardiopulmonary resuscitation (CPR). This will include any and
                all medical efforts that are indicated during arrest, including
                artificial ventilation and efforts to restore and/or stabilize
                cardiopulmonary function.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                molstForm.cprStatus.optionA === 'intubate'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                  molstForm.cprStatus.optionA === 'intubate'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                    molstForm.cprStatus.optionA === 'intubate'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">1b)</span>
                No CPR, Option A-1, Intubate: Comprehensive efforts may include
                intubation and artificial ventilation.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                molstForm.cprStatus.optionA === 'doNotIntubate'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                  molstForm.cprStatus.optionA === 'doNotIntubate'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.cprStatus.cprOption === 'noCprOptionA' &&
                    molstForm.cprStatus.optionA === 'doNotIntubate'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">1c)</span>
                No CPR, Option A-2, Do Not Intubate (DNI): Comprehensive efforts
                may include limited ventilatory support by CPAP or BiPAP, but do
                not intubate.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.cprStatus.cprOption === 'noCprOptionB'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.cprStatus.cprOption === 'noCprOptionB'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.cprStatus.cprOption === 'noCprOptionB'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">1d)</span>
                No CPR, Option B, Palliative and Supportive Care: Prior to
                arrest, provide passive oxygen for comfort and control any
                external bleeding. Prior to arrest, provide medications for pain
                relief as needed, but no other medications. Do not intubate or
                use CPAP or BiPAP. If cardiac and/or pulmonary arrest occurs, do
                not attempt resuscitation (No CPR). Allow death to occur
                naturally.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Artificial Ventilation -->
      <div class="form-section">
        <h3 class="section-title">Section 2: Artificial Ventilation</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="
                molstForm.artificialVentilation.ventilationOption === 'yes'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.artificialVentilation.ventilationOption === 'yes'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.artificialVentilation.ventilationOption === 'yes'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">2a)</span>
                May use intubation and artificial ventilation indefinitely, if
                medically indicated.
              </span>
            </div>

            <div
              class="radio-item"
              [class.selected]="
                molstForm.artificialVentilation.ventilationOption === 'trial'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.artificialVentilation.ventilationOption === 'trial'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.artificialVentilation.ventilationOption ===
                    'trial'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">2b)</span>
                May use intubation and artificial ventilation as a limited
                therapeutic trial.
                <span
                  *ngIf="
                    molstForm.artificialVentilation.timeLimit &&
                    molstForm.artificialVentilation.ventilationOption ===
                      'trial'
                  "
                >
                  (Time Limit:
                  {{ molstForm.artificialVentilation.timeLimit }})
                </span>
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.artificialVentilation.ventilationOption ===
                'nonInvasiveOnly'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.artificialVentilation.ventilationOption ===
                  'nonInvasiveOnly'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.artificialVentilation.ventilationOption ===
                    'nonInvasiveOnly'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">2c)</span>
                May use only CPAP or BiPAP for artificial ventilation, as
                medically indicated.
                <span
                  *ngIf="
                    molstForm.artificialVentilation.timeLimit &&
                    molstForm.artificialVentilation.ventilationOption ===
                      'nonInvasiveOnly'
                  "
                >
                  (Time Limit:
                  {{ molstForm.artificialVentilation.timeLimit }})
                </span>
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.artificialVentilation.ventilationOption === 'no'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.artificialVentilation.ventilationOption === 'no'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.artificialVentilation.ventilationOption === 'no'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">2d)</span>
                Do not use any artificial ventilation (no intubation, CPAP or
                BiPAP).
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 3: Blood Transfusion -->
      <div class="form-section">
        <h3 class="section-title">Section 3: Blood Transfusion</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.bloodTransfusion === 'yes'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.bloodTransfusion === 'yes'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.bloodTransfusion === 'yes'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">3a)</span>
                May give any blood product (whole blood, packed red blood cells,
                plasma or platelets) that is medically indicated.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.bloodTransfusion === 'no'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.bloodTransfusion === 'no'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.bloodTransfusion === 'no'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">3b)</span>
                Do not give any blood products.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 4: Hospital Transfer -->
      <div class="form-section">
        <h3 class="section-title">Section 4: Hospital Transfer</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.hospitalTransfer === 'full'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.hospitalTransfer === 'full'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.hospitalTransfer === 'full'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">4a)</span>
                Transfer to hospital for any situation requiring hospital-level
                care.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.hospitalTransfer === 'limited'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.hospitalTransfer === 'limited'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.hospitalTransfer === 'limited'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">4b)</span>
                Transfer to hospital for severe pain or severe symptoms that
                cannot be controlled otherwise.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.hospitalTransfer === 'comfort'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.hospitalTransfer === 'comfort'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.hospitalTransfer === 'comfort'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">4c)</span>
                Do not transfer to hospital, but treat with options available
                outside the hospital.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 5: Medical Workup -->
      <div class="form-section">
        <h3 class="section-title">Section 5: Medical Workup</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.medicalWorkup === 'full'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.medicalWorkup === 'full'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.medicalWorkup === 'full'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">5a)</span>
                May perform any medical tests indicated to diagnose and/or treat
                a medical condition.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.medicalWorkup === 'limited'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.medicalWorkup === 'limited'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.medicalWorkup === 'limited'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">5b)</span>
                Only perform limited medical tests necessary for symptomatic
                treatment or comfort.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.medicalWorkup === 'comfort'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.medicalWorkup === 'comfort'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.medicalWorkup === 'comfort'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">5c)</span>
                Do not perform any medical tests for diagnosis or treatment.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 6: Antibiotics -->
      <div class="form-section">
        <h3 class="section-title">Section 6: Antibiotics</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.antibiotics === 'full'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.antibiotics === 'full'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.antibiotics === 'full'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">6a)</span>
                May use antibiotics (oral, intravenous or intramuscular) as
                medically indicated.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.antibiotics === 'limited'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.antibiotics === 'limited'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.antibiotics === 'limited'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">6b)</span>
                May use oral antibiotics when medically indicated, but do not
                give intravenous or intramuscular antibiotics.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.antibiotics === 'comfort'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.antibiotics === 'comfort'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.antibiotics === 'comfort'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">6c)</span>
                May use oral antibiotics only when indicated for symptom relief
                or comfort.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.antibiotics === 'none'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.antibiotics === 'none'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.antibiotics === 'none'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">6d)</span>
                Do not treat with antibiotics.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 7: Fluids and Nutrition -->
      <div class="form-section">
        <h3 class="section-title">
          Section 7: Artificially Administered Fluids and Nutrition
        </h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="
                molstForm.fluidsAndNutrition.fluidsOption === 'yes'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.fluidsAndNutrition.fluidsOption === 'yes'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.fluidsAndNutrition.fluidsOption === 'yes'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">7a)</span>
                May give artificially administered fluids and nutrition, even
                indefinitely, if medically indicated.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.fluidsAndNutrition.fluidsOption === 'middleGround'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.fluidsAndNutrition.fluidsOption === 'middleGround'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="
                    molstForm.fluidsAndNutrition.fluidsOption === 'middleGround'
                  "
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">7b)</span>
                May give artificially administered fluids and nutrition, if
                medically indicated, as a trial.
                <span
                  *ngIf="
                    molstForm.fluidsAndNutrition.timeLimit &&
                    molstForm.fluidsAndNutrition.fluidsOption === 'middleGround'
                  "
                >
                  (Time Limit: {{ molstForm.fluidsAndNutrition.timeLimit }} )
                </span>
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.fluidsAndNutrition.fluidsOption === 'trial'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.fluidsAndNutrition.fluidsOption === 'trial'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.fluidsAndNutrition.fluidsOption === 'trial'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">7c)</span>
                May give fluids for artificial hydration as a therapeutic trial,
                but do not give artificially administered nutrition.
                <span
                  *ngIf="
                    molstForm.fluidsAndNutrition.timeLimit &&
                    molstForm.fluidsAndNutrition.fluidsOption === 'trial'
                  "
                >
                  (Time Limit: {{ molstForm.fluidsAndNutrition.timeLimit }} )
                </span>
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="
                molstForm.fluidsAndNutrition.fluidsOption === 'no'
              "
            >
              <div
                class="radio-circle"
                [class.selected]="
                  molstForm.fluidsAndNutrition.fluidsOption === 'no'
                "
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.fluidsAndNutrition.fluidsOption === 'no'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">7d)</span>
                Do not provide artificially administered fluids or nutrition.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 8: Dialysis -->
      <div class="form-section">
        <h3 class="section-title">Section 8: Dialysis</h3>
        <div class="section-content">
          <div class="radio-display">
            <div
              class="radio-item"
              [class.selected]="molstForm.dialysis.dialysisOption === 'yes'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.dialysis.dialysisOption === 'yes'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.dialysis.dialysisOption === 'yes'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">8a)</span>
                May give chronic dialysis for end-stage kidney disease if
                medically indicated.
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.dialysis.dialysisOption === 'trial'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.dialysis.dialysisOption === 'trial'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.dialysis.dialysisOption === 'trial'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">8b)</span>
                May give dialysis for a limited period.
                <span *ngIf="molstForm.dialysis.timeLimit">
                  (Time Limit: {{ molstForm.dialysis.timeLimit }})
                </span>
              </span>
            </div>
            <div
              class="radio-item"
              [class.selected]="molstForm.dialysis.dialysisOption === 'no'"
            >
              <div
                class="radio-circle"
                [class.selected]="molstForm.dialysis.dialysisOption === 'no'"
              >
                <i
                  class="pi pi-circle-fill"
                  *ngIf="molstForm.dialysis.dialysisOption === 'no'"
                ></i>
              </div>
              <span class="radio-label">
                <span class="option-letter">8c)</span>
                Do not provide acute or chronic dialysis.
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 9: Other Orders -->
      <div class="form-section">
        <h3 class="section-title">Section 9: Other Orders</h3>
        <div class="section-content">
          <div
            class="text-display"
            *ngIf="molstForm.otherOrders; else noOtherOrders"
          >
            {{ molstForm.otherOrders }}
          </div>
          <ng-template #noOtherOrders>
            <span class="no-data">No additional orders specified</span>
          </ng-template>
        </div>
      </div>

      <!-- Practitioner Information -->
      <div class="form-section">
        <h3 class="section-title">Practitioner Information</h3>
        <div class="section-content">
          <div class="practitioner-info">
            <div class="info-row">
              <span class="info-label">Name:</span>
              <span class="info-value">{{ molstForm.practitioner.name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">License Number:</span>
              <span class="info-value">{{
                molstForm.practitioner.licenseNumber
              }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Phone Number:</span>
              <span class="info-value">{{
                molstForm.practitioner.phoneNumber
              }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date:</span>
              <span class="info-value">{{
                formatDate(molstForm.practitioner.date)
              }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Digital Signature:</span>
              <span class="info-value signature">{{
                molstForm.practitioner.signature
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Disclaimer -->
    <div class="disclaimer">
      <p>
        <strong>Important:</strong> This is a comprehensive view of the
        patient's MOLST form. Always verify the complete form and consult with
        the patient's primary care team before making treatment decisions.
      </p>
    </div>
  </div>
</div>
